// Per-edit canvas persistence. Every graph mutation applies to the canvas
// store instantly (the optimistic update) and persists via the node/edge CRUD
// API in the background — the same model every other surface already uses.
//
// Success: mirror the store into the ['canvas'] query cache (setQueryData, no
// network) so hydration-on-mount and the other views that read the canvas
// query see the persisted state.
//
// Failure: destructive toast + invalidate the canvas query. The refetch flows
// through ValuePropositionCanvas's hydrate-on-data-change effect, rewriting
// the store to server truth — rollback-by-refetch, no hand-written inverse
// ops. useSpineLayout keys on graph structure, so a content-equal rewrite
// doesn't re-trigger layout.

import { useMemo } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import type { Node, Edge, Connection } from '@xyflow/react';
import { addEdge as rfAddEdge } from '@xyflow/react';
import { useCanvasStore } from '@/stores';
import { api } from '@/services/api';
import { toApiNode, toUpdateNode, toApiEdge } from '@/services/canvas-mappers';
import { connectedEdgeIds, persistGraphDeletions } from '@/lib/canvas-persist';
import { toast } from '@/hooks/use-toast';
import type { ApiResponse } from '@/types/api';

async function expectSuccess(op: Promise<ApiResponse<unknown>>): Promise<void> {
  const res = await op;
  if (!res.success) throw new Error(res.message || 'Request failed');
}

export function useCanvasMutations(projectId: string) {
  const queryClient = useQueryClient();

  // Stable identities (everything reads the store via getState, so nothing here
  // closes over render state) — callers feed these into ReactFlow props.
  return useMemo(() => {
    const syncCache = () => {
      const { nodes, edges } = useCanvasStore.getState();
      queryClient.setQueryData(['canvas', projectId], { nodes, edges });
    };

    const rollback = (title: string) => (err: unknown) => {
      toast({
        title,
        description: err instanceof Error ? err.message : '',
        variant: 'destructive',
      });
      void queryClient.invalidateQueries({ queryKey: ['canvas', projectId] });
    };

    const addNode = (node: Node) => {
      // Refuse adds until the canvas query has loaded. The canvas renders even
      // when the query *errors* (only isLoading shows the spinner), so an add
      // could otherwise land in a store that was never hydrated for this
      // project — persisted server-side but invisible, or appended to a stale
      // graph.
      if (queryClient.getQueryState(['canvas', projectId])?.status !== 'success') {
        toast({ title: 'Canvas still loading', description: 'Try again in a moment.' });
        return;
      }
      useCanvasStore.getState().addNode(node);
      expectSuccess(api.createNode(toApiNode(node, projectId)))
        .then(syncCache)
        .catch(rollback('Failed to add node'));
    };

    const updateNodeData = (id: string, data: { label: string; content: string }) => {
      useCanvasStore.getState().updateNode(id, data);
      const node = useCanvasStore.getState().nodes.find((n) => n.id === id);
      if (!node) return;
      expectSuccess(api.updateNode(toUpdateNode(node, projectId)))
        .then(syncCache)
        .catch(rollback('Failed to save node'));
    };

    const deleteNode = (id: string) => {
      const edgeIds = connectedEdgeIds(id, useCanvasStore.getState().edges);
      useCanvasStore.getState().removeNode(id); // also drops connected edges locally
      persistGraphDeletions(api, projectId, [id], edgeIds)
        .then(syncCache)
        .catch(rollback('Failed to delete node'));
    };

    // Takes the fully-decorated edge (label/edgeType/visuals) built at the call
    // site; rfAddEdge assigns the id and dedupes — a connection identical to an
    // existing edge is dropped, in which case there's nothing to persist.
    const connectEdge = (edge: Edge | Connection) => {
      const store = useCanvasStore.getState();
      const withNew = rfAddEdge(edge, store.edges);
      if (withNew.length === store.edges.length) return;
      const added = withNew[withNew.length - 1];
      store.setEdges(withNew);
      expectSuccess(api.createEdge(toApiEdge(added, projectId)))
        .then(syncCache)
        .catch(rollback('Failed to add edge'));
    };

    // Persist path for React Flow's Delete key. RF has already removed the
    // elements from the store via onNodesChange/onEdgesChange and reports a
    // deleted node's connected edges — but only the ones in its (filtered)
    // view. An edge hidden by a canvas filter is neither removed nor reported,
    // so sweep the store for edges still touching a deleted node and delete
    // those too.
    const persistRemoved = (nodes: Node[], edges: Edge[]) => {
      if (nodes.length === 0 && edges.length === 0) return;
      const store = useCanvasStore.getState();
      // Set-dedupe: a hidden edge between two deleted nodes is found once per
      // node. No overlap with RF-reported edges — those are already out of the
      // store when onDelete fires.
      const hidden = new Set(nodes.flatMap((n) => connectedEdgeIds(n.id, store.edges)));
      if (hidden.size > 0) {
        store.setEdges(store.edges.filter((e) => !hidden.has(e.id)));
      }
      persistGraphDeletions(
        api,
        projectId,
        nodes.map((n) => n.id),
        [...edges.map((e) => e.id), ...hidden],
      )
        .then(syncCache)
        .catch(rollback('Failed to delete from graph'));
    };

    return { addNode, updateNodeData, deleteNode, connectEdge, persistRemoved };
  }, [projectId, queryClient]);
}
